import { useCallback, useEffect, useRef, useState } from 'react';
import { Modal } from 'antd';
import { BellOutlined, CloseOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

import './index.scss';

// How long each notice holds before the next slides in.
const SLIDE_MS = 2000;

// Matches the caps in index.scss. The panel is sized here in JS as well so the frame can be
// animated between two posters of different shapes instead of snapping.
const MAX_W = 620;
const WIDTH_RATIO = 0.94;
const HEIGHT_RATIO = 0.72;

const prefersReducedMotion = () =>
  typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// The size a poster will actually occupy: scaled down to fit the viewport, never scaled up past its
// own resolution.
const fitted = ({ w, h }, view) => {
  const scale = Math.min((Math.min(MAX_W, view.w * WIDTH_RATIO)) / w, (view.h * HEIGHT_RATIO) / h, 1);

  return { width: Math.round(w * scale), height: Math.round(h * scale) };
};

/**
 * The notice a client sees on sign-in.
 *
 * Framed as a notice, never as an advert: no "ad" chip, no expiry window, and no `body` text. The
 * admin composes a finished poster with the wording baked in - `body` is routinely a placeholder
 * like "AD" - so printing it as a heading only fights the artwork.
 *
 * Chrome is one white bar carrying the title, the paging and the close, over a frame that is exactly
 * the artwork. Posters rarely share an aspect ratio, so the frame is measured and animated between
 * slides; letting it snap from one shape to the next is the thing that reads as broken.
 */
const NoticeModal = ({ notices, onClose }) => {
  const { t } = useTranslation('common');
  const [visible, setVisible] = useState(true);
  const [index, setIndex] = useState(0);
  const [sizes, setSizes] = useState({});
  const [view, setView] = useState({ w: window.innerWidth, h: window.innerHeight });
  const lastShownAt = useRef(0);

  const many = notices.length > 1;

  // Measure every poster up front. Paging then never waits on the network, and the panel is only
  // ever shown at a size that matches the artwork inside it.
  useEffect(() => {
    let live = true;

    notices.forEach((notice) => {
      const probe = new Image();

      probe.onload = () =>
        live &&
        setSizes((current) => ({
          ...current,
          [notice.id]: { w: probe.naturalWidth, h: probe.naturalHeight },
        }));
      probe.src = notice.image_url;
    });

    return () => {
      live = false;
    };
  }, [notices]);

  useEffect(() => {
    const onResize = () => setView({ w: window.innerWidth, h: window.innerHeight });

    window.addEventListener('resize', onResize);

    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Every slide gets its full two seconds on screen. The floor is enforced here rather than trusted
  // to the timer alone: a second timer left behind by a hot reload, or any double-mount, would
  // otherwise advance the deck early and the posters would appear to race past.
  const showSlide = useCallback((next) => {
    const now = performance.now();
    if (now - lastShownAt.current < SLIDE_MS - 50) return;

    lastShownAt.current = now;
    setIndex(next);
  }, []);

  useEffect(() => {
    if (!many || prefersReducedMotion()) return undefined;

    // Keyed on `index`, so a re-render can only ever restart the wait - never stack up timers and
    // race the slides past. A plain interval flickers if the effect is re-entered.
    const timer = setTimeout(() => showSlide((index + 1) % notices.length), SLIDE_MS);

    return () => clearTimeout(timer);
  }, [index, many, notices.length, showSlide]);

  const dismiss = () => {
    setVisible(false);
    onClose();
  };

  // Hold the panel back until the first poster is measured, so a notice never appears as an empty
  // frame that then jumps to its real size.
  const firstSize = sizes[notices[0].id];
  if (!firstSize) return null;

  const frame = fitted(sizes[notices[index].id] || firstSize, view);

  return (
    <Modal
      /* The panel is as wide as the poster, so it never ends in a band of empty colour. */
      width={frame.width}
      visible={visible}
      onCancel={dismiss}
      footer={null}
      closable={false}
      centered
      destroyOnClose
      wrapClassName="notice-modal"
      bodyStyle={{ padding: 0 }}
    >
      <div className="notice-modal__bar">
        <span className="notice-modal__title">
          <BellOutlined />
          <span>{t('notice')}</span>
        </span>

        {many && (
          <span className="notice-modal__tools">
            <span className="notice-modal__dots">
              {notices.map((notice, i) => (
                <button
                  key={notice.id}
                  type="button"
                  className={i === index ? 'is-current' : ''}
                  /* A tap is intentional, so it lands at once - but it still resets the clock, so
                     the slide the client chose gets its full two seconds before moving on. */
                  onClick={() => {
                    lastShownAt.current = performance.now();
                    setIndex(i);
                  }}
                  aria-label={`${i + 1} / ${notices.length}`}
                  aria-current={i === index}
                />
              ))}
            </span>
            <span className="notice-modal__count">
              {index + 1} / {notices.length}
            </span>
          </span>
        )}

        <button type="button" className="notice-modal__close" onClick={dismiss} aria-label={t('close')}>
          <CloseOutlined />
        </button>
      </div>

      {/* No pause on hover: the panel opens centred under wherever the pointer already is, so
          pausing there would strand most clients on slide one. */}
      <div className="notice-modal__frame" style={{ height: frame.height }}>
        {notices.map((notice, i) => (
          <img
            key={notice.id}
            src={notice.image_url}
            /* `body` is the only text the record carries, so it describes the image for screen
               readers even though it is never rendered as copy. */
            alt={notice.body}
            className={`notice-modal__art ${i === index ? 'is-current' : ''}`}
          />
        ))}
      </div>
    </Modal>
  );
};

export default NoticeModal;
